/**
 * Jazz-backed implementation of ProjectStore. Reads from Account.root.projects,
 * uses Jazz Groups for access control, and serves example metadata from
 * @diagram/sim-examples.
 */
import { co, Group, Account, type ID } from "jazz-tools";
import {
  DiagramProject,
  FileEntry,
  AuthorizedAccount,
  AuthorizedAccountList,
  createTextUpdateLog,
  type VibeDiagramAccount,
} from "../jazz/schema";
import { exampleProjects, type ExampleMeta } from "@diagram/sim-examples";
import type {
  AuthorizedAccountEntry,
  ProjectStore,
  ProjectSummary,
  Role,
  Unsubscribe,
} from "./ProjectStore";

const FileList = co.list(FileEntry);

/**
 * A file with its collaborative-text log already in place.
 *
 * Files created without one get it bootstrapped when a writer first opens
 * them — and two tabs opening the same new project at once both bootstrap,
 * because neither has seen the other's write yet. Last-write-wins then
 * leaves one of them editing a log nobody else reads. Creating the log up
 * front removes that race for every project made from here on; only projects
 * that predate the update log still have to be promoted on open.
 */
function newFileEntry(group: Group, path: string, content: string): FileEntry {
  return FileEntry.create(
    {
      path,
      content: co.plainText().create(content, { owner: group }),
      updates: createTextUpdateLog(group, content),
    },
    { owner: group },
  );
}

/**
 * Shared empty results. Returning a fresh `[]` each time would break the
 * port's stable-snapshot contract for the not-yet-loaded case.
 */
const EMPTY_PROJECTS: ProjectSummary[] = [];
const EMPTY_AUTHORIZED: AuthorizedAccountEntry[] = [];

/**
 * Cheap fingerprint of every mutable field `listProjects` exposes. A change
 * to it means the cached array must be rebuilt. `createdAt` is deliberately
 * absent: it is written once at creation and never mutated, so including it
 * would only lengthen the string.
 */
function summarySignature(
  projects: NonNullable<
    NonNullable<co.loaded<typeof VibeDiagramAccount>["root"]>["projects"]
  >,
): string {
  const parts: string[] = [];
  for (let i = 0; i < projects.length; i++) {
    const p = projects[i];
    if (!p) {
      parts.push("");
      continue;
    }
    parts.push(
      `${p.$jazz.id}:${p.title}:${p.description ?? ""}:${p.updatedAt}:${p.$jazz.owner.myRole() ?? ""}`,
    );
  }
  return parts.join("|");
}

/** Map Jazz group role strings to our Role type. */
export function toRole(jazzRole: string | undefined): Role | undefined {
  if (jazzRole === "admin") return "admin";
  if (jazzRole === "writer") return "writer";
  if (jazzRole === "reader") return "reader";
  return undefined;
}

export class JazzProjectStore implements ProjectStore {
  /**
   * Cached list reads, keyed by a cheap signature of the underlying data.
   *
   * The port contract (see "Backend ports" in `architecture.md`) requires a
   * read to return the same reference until something actually changes;
   * rebuilding the array on every call would make `useSyncExternalStore`
   * loop forever. Same signature trick `JazzAnnotationsBackend` uses.
   */
  private projectsCache?: { signature: string; value: ProjectSummary[] };
  private authorizedCache?: {
    signature: string;
    value: AuthorizedAccountEntry[];
  };

  constructor(private readonly account: co.loaded<typeof VibeDiagramAccount>) {}

  /** Load a Jazz Account by ID string, returning null if not found. */
  private async loadAccount(accountId: string): Promise<Account | null> {
    // Using Account.load directly since we're loading a foreign account, not our own schema
    // eslint-disable-next-line @typescript-eslint/no-deprecated, @typescript-eslint/no-unsafe-type-assertion -- loading foreign accounts by ID
    return Account.load(accountId as unknown as ID<Account>, {
      loadAs: this.account,
    });
  }

  /** Add all authorized accounts from settings to a newly created group. */
  private addAuthorizedMembers(group: Group): void {
    const authorizedAccounts = this.account.root?.authorizedAccounts;
    if (!authorizedAccounts) return;
    for (let i = 0; i < authorizedAccounts.length; i++) {
      const entry = authorizedAccounts[i];
      if (!entry) continue;
      const { accountId, role } = entry;
      if (!accountId) continue;
      void this.loadAccount(accountId).then((account) => {
        if (account) group.addMember(account, role);
      });
    }
  }

  listProjects(): ProjectSummary[] {
    const projects = this.account.root?.projects;
    if (!projects) return EMPTY_PROJECTS;

    const signature = summarySignature(projects);
    if (this.projectsCache?.signature === signature) {
      return this.projectsCache.value;
    }

    const result: ProjectSummary[] = [];
    for (let i = 0; i < projects.length; i++) {
      const p = projects[i];
      if (!p) continue;
      const group = p.$jazz.owner;
      result.push({
        id: p.$jazz.id,
        title: p.title,
        description: p.description ?? undefined,
        createdAt: p.createdAt,
        updatedAt: p.updatedAt,
        role: toRole(group.myRole()) ?? "reader",
        isExample: false,
      });
    }
    this.projectsCache = { signature, value: result };
    return result;
  }

  createProject(
    title: string,
    files?: { path: string; content: string }[],
  ): string | null {
    const projects = this.account.root?.projects;
    if (!projects) return null;

    const group = Group.create();
    this.addAuthorizedMembers(group);

    const fileEntries = (files ?? [{ path: "/main.ts", content: "" }]).map(
      (f) => newFileEntry(group, f.path, f.content),
    );

    const fileList = FileList.create(fileEntries, { owner: group });
    const now = new Date().toISOString();
    const project = DiagramProject.create(
      {
        title,
        files: fileList,
        createdAt: now,
        updatedAt: now,
      },
      { owner: group },
    );

    projects.$jazz.push(project);
    return project.$jazz.id;
  }

  deleteProject(id: string): void {
    const projects = this.account.root?.projects;
    if (!projects) return;

    for (let i = 0; i < projects.length; i++) {
      const p = projects[i];
      if (p?.$jazz.id === id) {
        const role = toRole(p.$jazz.owner.myRole());
        if (role !== "admin") return;
        projects.$jazz.splice(i, 1);
        return;
      }
    }
  }

  forkProject(
    id: string,
    loadedDocument?: co.loaded<typeof DiagramProject>,
  ): string | null {
    const projects = this.account.root?.projects;
    if (!projects) return null;

    // Use the provided loaded document, or search the user's project list
    let source: co.loaded<typeof DiagramProject> | undefined = loadedDocument;
    if (!source) {
      for (let i = 0; i < projects.length; i++) {
        const p = projects[i];
        if (p?.$jazz.id === id) {
          source = p;
          break;
        }
      }
    }
    if (!source?.files) return null;

    const filesToCopy: { path: string; content: string }[] = [];
    for (let i = 0; i < source.files.length; i++) {
      const entry = source.files[i];
      if (!entry?.path) continue;
      const content = entry.content?.toString();
      if (content === undefined) {
        throw new Error(
          `Cannot fork: file "${entry.path}" content is not loaded`,
        );
      }
      filesToCopy.push({ path: entry.path, content });
    }

    const group = Group.create();
    this.addAuthorizedMembers(group);

    const fileEntries = filesToCopy.map((f) =>
      newFileEntry(group, f.path, f.content),
    );
    const fileList = FileList.create(fileEntries, { owner: group });
    const now = new Date().toISOString();
    const newProject = DiagramProject.create(
      {
        title: `${source.title} (fork)`,
        files: fileList,
        createdAt: now,
        updatedAt: now,
        forkedFrom: id,
      },
      { owner: group },
    );

    projects.$jazz.push(newProject);
    return newProject.$jazz.id;
  }

  getRole(id: string): Role | undefined {
    const projects = this.account.root?.projects;
    if (!projects) return undefined;

    for (let i = 0; i < projects.length; i++) {
      const p = projects[i];
      if (p?.$jazz.id === id) {
        return toRole(p.$jazz.owner.myRole());
      }
    }
    return undefined;
  }

  /** Grant an account access to all existing projects. */
  async addAccountToAllProjects(
    accountId: string,
    role: "reader" | "writer" | "admin",
  ): Promise<void> {
    const account = await this.loadAccount(accountId);
    if (!account) return;
    const projects = this.account.root?.projects;
    if (!projects) return;
    for (let i = 0; i < projects.length; i++) {
      const p = projects[i];
      if (!p) continue;
      p.$jazz.owner.addMember(account, role);
    }
  }

  /** Remove an account from all existing projects. */
  async removeAccountFromAllProjects(accountId: string): Promise<void> {
    const account = await this.loadAccount(accountId);
    if (!account) return;
    const projects = this.account.root?.projects;
    if (!projects) return;
    for (let i = 0; i < projects.length; i++) {
      const p = projects[i];
      if (!p) continue;
      p.$jazz.owner.removeMember(account);
    }
  }

  listAuthorizedAccounts(): AuthorizedAccountEntry[] {
    const list = this.account.root?.authorizedAccounts;
    if (!list) return EMPTY_AUTHORIZED;

    const parts: string[] = [];
    for (let i = 0; i < list.length; i++) {
      const entry = list[i];
      parts.push(`${entry?.accountId ?? ""}:${entry?.role ?? ""}`);
    }
    const signature = parts.join("|");
    if (this.authorizedCache?.signature === signature) {
      return this.authorizedCache.value;
    }

    const result: AuthorizedAccountEntry[] = [];
    for (let i = 0; i < list.length; i++) {
      const entry = list[i];
      if (!entry?.accountId) continue;
      result.push({ accountId: entry.accountId, role: entry.role });
    }
    this.authorizedCache = { signature, value: result };
    return result;
  }

  // eslint-disable-next-line @typescript-eslint/require-await -- async so the port stays uniform; the grant is intentionally not awaited
  async authorizeAccount(accountId: string, role: Role): Promise<boolean> {
    const trimmed = accountId.trim();
    if (!trimmed) return false;

    const root = this.account.root;
    if (!root) return false;

    const list = root.authorizedAccounts;
    for (let i = 0; i < (list?.length ?? 0); i++) {
      if (list?.[i]?.accountId === trimmed) return false;
    }

    // Created on first use, owned by the account itself — this is personal
    // settings data rather than something shared through a project Group.
    let target = list;
    if (!target) {
      // eslint-disable-next-line @typescript-eslint/no-deprecated -- personal account-level data
      target = AuthorizedAccountList.create([], { owner: this.account });
      root.$jazz.set("authorizedAccounts", target);
    }

    // eslint-disable-next-line @typescript-eslint/no-deprecated -- personal account-level data
    const entry = AuthorizedAccount.create(
      { accountId: trimmed, role },
      { owner: this.account },
    );
    target.$jazz.push(entry);

    // Fire-and-forget, as the component did before this moved into the
    // store: granting access walks every project and loads a foreign
    // account, which is slow for a reachable id and never resolves for an
    // unreachable one. Awaiting it here would make the caller's "did the add
    // happen?" answer wait on the network — the settings panel would sit
    // with the typed id still in the field, looking like the add failed.
    void this.addAccountToAllProjects(trimmed, role);
    return true;
  }

  async revokeAccount(accountId: string): Promise<void> {
    const list = this.account.root?.authorizedAccounts;
    if (!list) return;
    for (let i = 0; i < list.length; i++) {
      if (list[i]?.accountId !== accountId) continue;
      list.$jazz.splice(i, 1);
      break;
    }
    await this.removeAccountFromAllProjects(accountId);
  }

  getExampleProjects(): ProjectSummary[] {
    return exampleProjects.map((ex: ExampleMeta) => ({
      id: ex.id,
      title: ex.title,
      description: ex.description,
      createdAt: "",
      updatedAt: "",
      role: "reader" as Role,
      isExample: true,
    }));
  }

  onProjectListChange(cb: () => void): Unsubscribe {
    const projects = this.account.root?.projects;
    if (!projects) return () => {};

    const unsub = projects.$jazz.subscribe({}, () => {
      cb();
    });
    return unsub;
  }
}
